import "./style.scss";

import ModalBox from "components/Modal";
import StyledButton from "components/shared/StyledButton";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { connectDocoon, fetchIntegrations } from "store/integrationsStore";
import { useAppDispatch } from "store/store";

import type { TDocoonConnectModal } from "../types";

interface IDocoonConnectModal {
    data: TDocoonConnectModal;
    onClose: () => void;
}

interface DocoonFormValues {
    login: string;
    pwd: string;
    docoon_url: string;
}

const DocoonConnectModal = ({ data, onClose }: IDocoonConnectModal) => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();
    const [isLoading, setIsLoading] = useState(false);
    const [showPwd, setShowPwd] = useState(false);

    const {
        register,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm<DocoonFormValues>();

    const handleClose = () => {
        reset();
        onClose();
    };

    const onSubmit = async (values: DocoonFormValues) => {
        setIsLoading(true);
        const sanitized = { ...values, docoon_url: values.docoon_url.trim().replace(/\?+$/, "") };
        const response = await dispatch(connectDocoon(sanitized));
        if (response.meta.requestStatus === "fulfilled") {
            await dispatch(fetchIntegrations());
            handleClose();
        }
        setIsLoading(false);
    };

    return (
        <ModalBox
            open={data.open}
            onClose={handleClose}
            mainTitle={t("DocoonConnectModal.Title")}
            subTitle={t("DocoonConnectModal.Subtitle")}
        >
            <form onSubmit={handleSubmit(onSubmit)} className="docoon-connect-modal">
                <div className="docoon-connect-modal-fields">
                    <div className="docoon-connect-modal-field">
                        <label className="docoon-connect-modal-label">{t("DocoonConnectModal.Login")}</label>
                        <input
                            className={`docoon-connect-modal-input${errors.login ? " error" : ""}`}
                            placeholder={t("DocoonConnectModal.LoginPlaceholder")}
                            {...register("login", { required: true })}
                        />
                        {errors.login && (
                            <span className="docoon-connect-modal-error">{t("DocoonConnectModal.FieldRequired")}</span>
                        )}
                    </div>

                    <div className="docoon-connect-modal-field">
                        <label className="docoon-connect-modal-label">{t("DocoonConnectModal.Password")}</label>
                        <div style={{ position: "relative", display: "flex" }}>
                            <input
                                type={showPwd ? "text" : "password"}
                                className={`docoon-connect-modal-input${errors.pwd ? " error" : ""}`}
                                placeholder={t("DocoonConnectModal.PasswordPlaceholder")}
                                style={{ flex: 1, paddingRight: 40 }}
                                {...register("pwd", { required: true })}
                            />
                            <button
                                type="button"
                                onClick={() => setShowPwd((v) => !v)}
                                tabIndex={-1}
                                style={{
                                    position: "absolute",
                                    right: 10,
                                    top: "50%",
                                    transform: "translateY(-50%)",
                                    background: "none",
                                    border: "none",
                                    padding: 0,
                                    cursor: "pointer",
                                    color: "#9ca3af",
                                    display: "flex",
                                    alignItems: "center",
                                }}
                            >
                                {showPwd ? (
                                    <svg
                                        width="18"
                                        height="18"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                                        <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                                        <line x1="1" y1="1" x2="23" y2="23" />
                                    </svg>
                                ) : (
                                    <svg
                                        width="18"
                                        height="18"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                        <circle cx="12" cy="12" r="3" />
                                    </svg>
                                )}
                            </button>
                        </div>
                        {errors.pwd && (
                            <span className="docoon-connect-modal-error">{t("DocoonConnectModal.FieldRequired")}</span>
                        )}
                    </div>

                    <div className="docoon-connect-modal-field">
                        <label className="docoon-connect-modal-label">{t("DocoonConnectModal.DocoonUrl")}</label>
                        <input
                            className={`docoon-connect-modal-input${errors.docoon_url ? " error" : ""}`}
                            placeholder={t("DocoonConnectModal.DocoonUrlPlaceholder")}
                            {...register("docoon_url", { required: true })}
                        />
                        {errors.docoon_url && (
                            <span className="docoon-connect-modal-error">{t("DocoonConnectModal.FieldRequired")}</span>
                        )}
                    </div>
                </div>

                <div className="docoon-connect-modal-actions">
                    <StyledButton buttonType="primary" type="submit" fullWidth loading={isLoading}>
                        {t("DocoonConnectModal.Connect")}
                    </StyledButton>
                    <StyledButton buttonType="secondary" fullWidth onClick={handleClose} disabled={isLoading}>
                        {t("IntegrationsSettings.NoCancel")}
                    </StyledButton>
                </div>
            </form>
        </ModalBox>
    );
};

export default DocoonConnectModal;
